"use client";

import { useCallback, useEffect, useState } from "react";
import { Check, Notebook, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TextArea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetBody,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";

interface TravelerProfilePanelProps {
  isOpen: boolean;
  onClose: () => void;
  refreshTrigger?: number;
}

export default function TravelerProfilePanel({
  isOpen,
  onClose,
  refreshTrigger,
}: TravelerProfilePanelProps) {
  const [profile, setProfile] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editText, setEditText] = useState("");
  const [saving, setSaving] = useState(false);

  const fetchProfile = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch("/api/planner/traveler-profile");
      if (!response.ok) return;
      const data = await response.json();
      setProfile(data.traveler_profile || "");
    } catch {
      // Fetch failed silently
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchProfile();
    }
  }, [isOpen, fetchProfile, refreshTrigger]);

  const handleEdit = () => {
    setEditText(profile);
    setEditing(true);
  };

  const handleCancel = () => {
    setEditing(false);
    setEditText("");
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await fetch("/api/planner/traveler-profile", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ traveler_profile: editText }),
      });
      if (response.ok) {
        const data = await response.json();
        setProfile(data.traveler_profile || editText);
        setEditing(false);
      }
    } catch {
      // Save failed silently
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  const profileLines = profile
    .split("\n")
    .filter((line) => line.trim().length > 0);

  const editButton = !editing && profile && (
    <Button
      variant="ghost"
      size="icon-sm"
      onClick={handleEdit}
      className="text-muted-foreground hover:text-foreground"
    >
      <Pencil className="h-3.5 w-3.5" />
    </Button>
  );

  const desktopHeader = (
    <>
      <div className="flex items-center gap-2">
        <Notebook className="h-4 w-4 text-primary" />
        <h2 className="text-sm font-semibold text-foreground">
          Traveler Profile
        </h2>
      </div>
      {editButton}
    </>
  );

  const sheetHeaderInner = (
    <>
      <div className="flex items-center gap-2">
        <Notebook className="h-4 w-4 text-primary" />
        <SheetTitle>Traveler Profile</SheetTitle>
      </div>
      {editButton}
    </>
  );

  const body = loading ? (
    <div className="flex items-center justify-center py-8">
      <div className="h-5 w-5 animate-spin rounded-full border-2 border-muted-foreground border-t-primary" />
    </div>
  ) : editing ? (
    <div className="flex flex-col gap-2">
      <TextArea
        value={editText}
        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
          setEditText(e.target.value)
        }
        rows={12}
        className="min-h-[200px] resize-y bg-muted text-sm"
        placeholder="Enter your travel preferences, one per line..."
      />
      <div className="flex gap-2">
        <Button
          size="sm"
          onClick={handleSave}
          disabled={saving}
          className="flex-1 gap-1"
        >
          <Check className="h-3.5 w-3.5" />
          {saving ? "Saving..." : "Save"}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleCancel}
          disabled={saving}
        >
          Cancel
        </Button>
      </div>
    </div>
  ) : profileLines.length === 0 ? (
    <div className="flex flex-col items-center gap-2 py-8 text-center">
      <Notebook className="h-8 w-8 text-muted-foreground/50" />
      <p className="text-sm text-muted-foreground">
        Your traveler profile is empty.
      </p>
      <p className="text-xs text-muted-foreground/70">
        As you plan trips and share preferences, I&apos;ll learn what you like.
      </p>
    </div>
  ) : (
    <div className="space-y-1.5">
      {profileLines.map((line, i) => {
        const text = line.replace(/^-\s*/, "").trim();
        return (
          <div
            key={i}
            className="rounded-lg border border-border bg-card px-3 py-2"
          >
            <p className="text-sm text-foreground">{text}</p>
          </div>
        );
      })}
      <Button
        variant="ghost"
        size="sm"
        onClick={handleEdit}
        className="mt-2 w-full gap-1 text-muted-foreground hover:text-foreground"
      >
        <Pencil className="h-3 w-3" />
        Edit profile
      </Button>
    </div>
  );

  return (
    <>
      {/* Desktop: inline right-rail flex child */}
      <div className="hidden h-full w-72 flex-col border-l border-border bg-background md:flex">
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          {desktopHeader}
        </div>
        <div className="flex-1 overflow-y-auto px-3 py-3">{body}</div>
      </div>

      {/* Mobile: Sheet overlay. Always rendered (open/closed controlled by
          Radix's `open` prop) so that unmounting ItineraryPanel's Sheet
          portal doesn't race with this panel's Sheet mounting in the same
          React commit. `modal={false}` disables focus-trap / scroll-lock. */}
      <Sheet modal={false} open={isOpen} onOpenChange={(v) => !v && onClose()}>
        <SheetContent
          side="right"
          size="sm"
          className="md:hidden"
          overlayClassName="md:hidden"
          onPointerDownOutside={(e) => e.preventDefault()}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <SheetHeader className="flex flex-row items-center justify-between">
            {sheetHeaderInner}
          </SheetHeader>
          <SheetBody>{body}</SheetBody>
        </SheetContent>
      </Sheet>
    </>
  );
}
